import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import { FiMail } from "react-icons/fi";

import "../settings.css";
import SideBar from "../Components/SideBar/SideBar";
import { showSnackBar } from "../../../../../../store/common/snackBarSlice";
import { updateGeneralSettings } from "../../../../../../services/settings.services";
import { findSettingByType } from "../../../../../../helpers/helpers";
import { addUser } from "../../../../../../store/userSlice";

// The account's general setting for customer delivery emails: "enabled" or
// "disabled"; none means off.
export const DELIVERY_EMAILS = "deliveryEmails";

/**
 * Customer Delivery Emails, in Settings: whether customers get an email with
 * the driver's photos when a run delivers their order (a stop recorded
 * Delivered or Partly delivered on the delivery list). Off until the account
 * owner switches it on; it saves straight away. Only the account owner (a
 * parent user) has it: the settings menu leaves it out for helper users, and
 * this page sends them to their settings.
 */
function DeliveryEmailSettings() {
  const { t } = useTranslation("settings");
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const saved =
    findSettingByType(user?.general_settings, DELIVERY_EMAILS)?.value ===
    "enabled";
  const [on, setOn] = useState(saved);
  const [saving, setSaving] = useState(false);
  useEffect(() => setOn(saved), [saved]);

  if (user?.parent_id) return <Navigate to="/settings/time-zone" replace />;

  const save = async (next) => {
    setOn(next);
    setSaving(true);
    const response = await updateGeneralSettings({
      setting_type: DELIVERY_EMAILS,
      value: next ? "enabled" : "disabled",
    });
    setSaving(false);
    if (!response?.data?.success) {
      setOn(!next);
      dispatch(
        showSnackBar({
          text: t("deliveryEmails.saveFailed"),
          severity: "error",
        }),
      );
      return;
    }
    const updatedUser = { ...user, general_settings: response.data.result };
    dispatch(addUser(updatedUser));
    try {
      localStorage.setItem("user", JSON.stringify(updatedUser));
    } catch {
      // The setting is saved; it just isn't kept in this browser.
    }
    dispatch(
      showSnackBar({
        text: next
          ? t("deliveryEmails.turnedOn")
          : t("deliveryEmails.turnedOff"),
        severity: "success",
      }),
    );
  };

  return (
    <div className="parent_wraper">
      <SideBar active="3_2" />
      <div className="dash_table_wrapper">
        <div className="mb-6 flex items-center justify-between px-1">
          <h2 className="m-0 text-lg font-semibold text-ui-text">
            {t("sidebar.items.deliveryEmails")}
          </h2>
        </div>

        <div className="max-w-[520px] rounded-xl border border-ui-border bg-ui-surface p-[16px]">
          <div className="flex items-start gap-[12px]">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-light">
              <FiMail size={14} className="text-brand" />
            </div>
            <div className="min-w-[0px] flex-1">
              <p
                id="deliveryEmailsLabel"
                className="m-0 text-sm font-semibold text-ui-text"
              >
                {t("deliveryEmails.label")}
              </p>
              <p
                id="deliveryEmailsHelp"
                className="m-[0px] mt-[4px] text-xs leading-[1.5] text-ui-text-muted"
              >
                {t("deliveryEmails.help")}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-labelledby="deliveryEmailsLabel"
              aria-describedby="deliveryEmailsHelp"
              disabled={saving}
              onClick={() => save(!on)}
              className={`relative mt-[2px] h-[24px] w-[42px] shrink-0 rounded-full border-0 p-[0px] transition-colors disabled:cursor-wait disabled:opacity-60 ${
                on ? "bg-brand" : "bg-ui-border-strong"
              }`}
            >
              <span
                aria-hidden="true"
                className={`absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow-ui-sm transition-[left] ${
                  on ? "left-[21px]" : "left-[3px]"
                }`}
              />
            </button>
          </div>
          <p className="m-[0px] mt-[12px] border-t border-ui-border-subtle pt-[10px] text-xs text-ui-text-muted">
            {on ? (
              <Trans
                t={t}
                i18nKey="deliveryEmails.on"
                components={{ b: <b className="text-state-success-text" /> }}
              />
            ) : (
              <Trans
                t={t}
                i18nKey="deliveryEmails.off"
                components={{ b: <b className="text-ui-text" /> }}
              />
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

export default DeliveryEmailSettings;
